import React, { useState, useEffect, useRef } from 'react';
import { fetchUsersPage, deleteUser, updateUserRole, toggleUserEnabled } from '../../services/adminUserService';
import AdminLayout from '../../components/admin/AdminLayout';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import { useNotification } from '../../components/framework/NotificationContext';
import { loadAllInBatches } from '../../utils/loadAllInBatches';

const BATCH_SIZE = 25;

const UserManagement = () => {
    const confirm = useConfirmation();
    const { success, error: showError } = useNotification();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, admin: 0, moderator: 0 });
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
    const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
    const [allLoaded, setAllLoaded] = useState(false);
    const loadTokenRef = useRef(0);

    useEffect(() => {
        loadUsers();
        return () => { loadTokenRef.current += 1; };
    }, []);

    useEffect(() => {
        const filtered = users.filter(user =>
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        filtered.sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        setFilteredUsers(filtered);
    }, [searchTerm, users, sortOrder]);

    // Recomputed as each background batch arrives, since users now loads progressively.
    useEffect(() => {
        const calculations = users.reduce((acc, user) => {
            acc.total++;
            if (user.role === 'ADMIN') acc.admin++;
            if (user.role === 'MODERATOR') acc.moderator++;
            return acc;
        }, { total: 0, admin: 0, moderator: 0 });
        setStats(calculations);
    }, [users]);

    const toggleSortOrder = () => {
        setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
    };

    // Renders the first batch immediately, then streams the rest in the background.
    const loadUsers = async () => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        setError(null);
        setUsers([]);
        setAllLoaded(false);
        setLoadProgress({ loaded: 0, total: 0 });

        let loadedCount = 0;

        try {
            await loadAllInBatches(
                (page, size) => fetchUsersPage(page, size),
                {
                    batchSize: BATCH_SIZE,
                    shouldContinue: () => loadTokenRef.current === myToken,
                    onBatch: (batch, meta) => {
                        if (loadTokenRef.current !== myToken) return;
                        loadedCount += batch.length;
                        setUsers(prev => [...prev, ...batch]);
                        setLoadProgress({ loaded: loadedCount, total: meta.totalElements });
                        if (meta.isFirst) {
                            setLoading(false);
                        }
                    }
                }
            );
            if (loadTokenRef.current === myToken) {
                setAllLoaded(true);
            }
        } catch (err) {
            if (loadTokenRef.current === myToken) {
                setError(err.message || "Failed to fetch users");
                setLoading(false);
            }
        }
    };

    const handleDelete = async (id) => {
        // Guard against mutating a user while background batches are still
        // being appended to `users` - avoids racing the load.
        if (!allLoaded) {
            showError('Please wait for all users to finish loading before taking action.');
            return;
        }
        const isConfirmed = await confirm({
            title: "Delete User",
            message: "Are you sure you want to delete this user? This action cannot be undone.",
            confirmLabel: "Delete User",
            type: "danger"
        });

        if (!isConfirmed) return;

        try {
            await deleteUser(id);
            setUsers(prev => prev.filter(u => u.id !== id));
            success("User deleted successfully");
        } catch (err) {
            showError(err.message || "Failed to delete user");
        }
    };

    const handleRoleChange = async (id, newRole) => {
        if (!allLoaded) {
            showError('Please wait for all users to finish loading before taking action.');
            return;
        }
        const isConfirmed = await confirm({
            title: "Change User Role",
            message: `Are you sure you want to change role to ${newRole}?`,
            confirmLabel: "Update Role",
            type: "warning"
        });

        if (!isConfirmed) return;

        try {
            await updateUserRole(id, newRole);
            setUsers(prev => prev.map(u => u.id === id ? { ...u, role: newRole } : u));
            success(`User role updated to ${newRole}`);
        } catch (err) {
            showError(err.message || "Failed to update role");
        }
    };

    const handleToggleEnabled = async (id, currentStatus) => {
        if (!allLoaded) {
            showError('Please wait for all users to finish loading before taking action.');
            return;
        }
        try {
            await toggleUserEnabled(id);
            setUsers(prev => prev.map(u => u.id === id ? { ...u, enabled: !currentStatus } : u));
        } catch (err) {
            showError(err.message || "Failed to update status");
        }
    };

    return (
        <AdminLayout
            title="User Management"
            subtitle="Manage platform users and their access levels."
        >
            {/* Summary Statistics Row */}
            <div className="dashboard-summary-row" style={{ marginBottom: '2rem' }}>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(0, 34, 68, 0.1)', color: '#002244' }}>
                        <i className="bi bi-people"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Total Users</span>
                        <span className="summary-value">{loading ? '-' : stats.total}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
                        <i className="bi bi-shield-lock"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Admins</span>
                        <span className="summary-value">{loading ? '-' : stats.admin}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(23, 162, 184, 0.1)', color: '#17a2b8' }}>
                        <i className="bi bi-shield-check"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Moderators</span>
                        <span className="summary-value">{loading ? '-' : stats.moderator}</span>
                    </div>
                </div>
            </div>

            <div className="admin-card">
                {/* Search and Toolbar */}
                <div className="admin-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
                    <div className="search-box" style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
                        <i className="bi bi-search" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}></i>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: '2.5rem', borderRadius: '8px' }}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {!allLoaded && loadProgress.total > 0 && (
                            <span className="text-muted small" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Loading {loadProgress.loaded} of {loadProgress.total}...
                            </span>
                        )}
                        <button className="btn btn-outline-primary" onClick={loadUsers} style={{ borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <i className="bi bi-arrow-clockwise"></i> Refresh List
                        </button>
                    </div>
                </div>

                {error && <div className="alert alert-danger" style={{ borderRadius: '8px' }}>{error}</div>}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading user directory...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>User Information</th>

                                    <th>Account Status</th>
                                    <th>Email Verification</th>
                                    <th>Permissions</th>
                                    <th
                                        onClick={toggleSortOrder}
                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                        title={`Sort by registration date/time (${sortOrder === 'desc' ? 'newest first' : 'oldest first'})`}
                                    >
                                        Registration Date
                                        <i
                                            className={`bi ${sortOrder === 'desc' ? 'bi-arrow-down' : 'bi-arrow-up'}`}
                                            style={{ marginLeft: '0.4rem' }}
                                        ></i>
                                    </th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                                            <i className="bi bi-person-x" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
                                            {searchTerm ? `No users found matching "${searchTerm}"` : 'No users registered yet.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 600, color: '#002244' }}>{user.name}</span>
                                                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>{user.email}</span>
                                                </div>
                                            </td>

                                            <td>
                                                <span className={`badge ${user.enabled ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`} style={{ border: '1px solid currentColor' }}>
                                                    {user.enabled ? 'Active' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${user.emailVerified ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}
                                                    style={{ border: '1px solid currentColor', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                                                >
                                                    <i className={`bi ${user.emailVerified ? 'bi-patch-check-fill' : 'bi-exclamation-triangle'}`}></i>
                                                    {user.emailVerified ? 'Verified' : 'Unverified'}
                                                </span>
                                            </td>
                                            <td>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={user.role}
                                                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                    disabled={!allLoaded}
                                                    title={allLoaded ? undefined : 'Waiting for all users to finish loading...'}
                                                    style={{
                                                        width: '130px',
                                                        borderRadius: '6px',
                                                        backgroundColor: user.role === 'ADMIN' ? '#fff5f5' : user.role === 'MODERATOR' ? '#f0faff' : '#fff'
                                                    }}
                                                >
                                                    <option value="USER">Standard User</option>
                                                    <option value="MODERATOR">Moderator</option>
                                                    <option value="ADMIN" style={{ fontWeight: 'bold', color: '#dc3545' }}>Administrator</option>
                                                </select>
                                            </td>
                                            <td className="mono-font" style={{ fontSize: '0.9rem' }}>
                                                {new Date(user.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                                {' '}
                                                <span style={{ color: '#6c757d' }}>
                                                    {new Date(user.createdAt).toLocaleTimeString('en-US', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                    <button
                                                        className={`btn btn-sm ${user.enabled ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                        onClick={() => handleToggleEnabled(user.id, user.enabled)}
                                                        disabled={!allLoaded}
                                                        title={allLoaded ? (user.enabled ? "Restrict User Access" : "Enable User Access") : 'Waiting for all users to finish loading...'}
                                                        style={{ width: '85px' }}
                                                    >
                                                        {user.enabled ? 'Disable' : 'Enable'}
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(user.id)}
                                                        disabled={!allLoaded}
                                                        title={allLoaded ? "Delete Account" : 'Waiting for all users to finish loading...'}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default UserManagement;
